import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import {
  CreateReleaseNoteDto,
  ReleaseNoteListItemType,
  ReleaseNote,
  UpdateReleaseNoteDto,
  PortalVersionDto,
  ReleasePortalType,
} from '../../types/release-notes/release-notes';
import { PaginationType } from '../../types/utils/pagination.type';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { setModalContent, setShowModal } from '../utils/utils.slice';
import { rejectWithApiError } from '../../utils/api';
import { RootReducerState } from '../../redux/Reducers';
import { refetchAllNotifications } from '../releaseNotifications/releaseNotifications.action';
import { USER_ROLE } from '../../types/enums/user-role.enum';

const appendMediaToFormData = (formData: FormData, media: File | File[] | undefined) => {
  if (Array.isArray(media)) {
    media.forEach(file => {
      if (file instanceof File) formData.append('files', file);
    });
  } else if (media instanceof File) {
    formData.append('files', media);
  }
};

export const postReleaseNoteDetails = createAsyncThunk<
  ReleaseNote,
  { data: CreateReleaseNoteDto & { media?: File | File[] }; fetchList: boolean },
  { state: RootReducerState; rejectValue: string }
>('releaseNotes/postReleaseNoteDetails', async ({ data, fetchList }, thunkAPI) => {
  try {
    const { media, ...payload } = data;
    const formData = new FormData();
    appendMediaToFormData(formData, media);
    formData.append('body', JSON.stringify(payload));

    const response = await axios.post<ReleaseNote>(`${process.env.REACT_APP_API_URL}/release-notes`, formData);

    if (fetchList) {
      thunkAPI.dispatch(getReleaseNotesList(thunkAPI.getState().releaseNotesReducer.releaseNotesRouteParams));
    }
    thunkAPI.dispatch(refetchAllNotifications());
    thunkAPI.dispatch(getPortalVersion());
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Could not create the release note!' });
  }
});

export const getReleaseNotesList = createAsyncThunk<
  PaginationType<ReleaseNoteListItemType>,
  Partial<RouteParamsType> & { controller?: AbortController },
  { state: RootReducerState; rejectValue: string }
>('releaseNotes/getReleaseNotesList/no-loader', async (data, thunkAPI) => {
  try {
    const { controller, ...params } = data;
    const userRole = thunkAPI.getState().authReducer.user?.role;
    const requestedPortalType = params.portal_type;
    const requestParams = { ...params };

    if (userRole === USER_ROLE.SUPERADMIN || userRole === USER_ROLE.TELCLOUD_ADMIN) {
      requestParams.portal_type = requestedPortalType || ReleasePortalType.TELCLOUD;
    } else {
      requestParams.portal_type = ReleasePortalType.TELCLOUD;
    }

    const filteredParams = Object.keys(requestParams).reduce<Record<string, string | number | boolean>>((acc, key) => {
      const value = requestParams[key as keyof typeof requestParams];

      if (value !== undefined && value !== '') {
        acc[key] = value as string | number | boolean;
      }

      return acc;
    }, {});

    const response = await axios.get<PaginationType<ReleaseNoteListItemType>>(
      `${process.env.REACT_APP_API_URL}/release-notes`,
      {
        params: filteredParams,
        signal: controller?.signal,
      }
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Could not fetch release notes!' });
  }
});

export const getReleaseNoteDetails = createAsyncThunk<
  ReleaseNote,
  { id: number },
  { state: RootReducerState; rejectValue: string }
>('releaseNotes/getReleaseNoteDetails', async ({ id }, thunkAPI) => {
  try {
    const response = await axios.get<ReleaseNote>(`${process.env.REACT_APP_API_URL}/release-notes/${id}`);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Could not fetch the release note!' });
  }
});

export const patchReleaseNoteDetails = createAsyncThunk<
  ReleaseNote,
  { id: number; data: UpdateReleaseNoteDto & { media?: File | File[] }; fetchList: boolean },
  { state: RootReducerState; rejectValue: string }
>('releaseNotes/patchReleaseNoteDetails', async ({ data, id, fetchList }, thunkAPI) => {
  try {
    const formData = new FormData();
    appendMediaToFormData(formData, data.media);
    formData.append('body', JSON.stringify(data));

    const response = await axios.patch<ReleaseNote>(`${process.env.REACT_APP_API_URL}/release-notes/${id}`, formData);

    if (fetchList) {
      thunkAPI.dispatch(getReleaseNotesList(thunkAPI.getState().releaseNotesReducer.releaseNotesRouteParams));
    }
    thunkAPI.dispatch(refetchAllNotifications());
    thunkAPI.dispatch(getPortalVersion());
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Could not update the release note!' });
  }
});

export const deleteReleaseNoteDetails = createAsyncThunk<
  ReleaseNote,
  { id: number; fetchList: boolean },
  { state: RootReducerState; rejectValue: string }
>('releaseNotes/deleteReleaseNoteDetails', async ({ id, fetchList }, thunkAPI) => {
  try {
    const response = await axios.delete<ReleaseNote>(`${process.env.REACT_APP_API_URL}/release-notes/${id}`);
    if (fetchList) {
      thunkAPI.dispatch(getReleaseNotesList(thunkAPI.getState().releaseNotesReducer.releaseNotesRouteParams));
    }
    thunkAPI.dispatch(refetchAllNotifications());
    thunkAPI.dispatch(getPortalVersion());
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Could not delete the release note!' });
  }
});

export const getPortalVersion = createAsyncThunk<
  PortalVersionDto,
  void,
  { state: RootReducerState; rejectValue: string }
>('releaseNotes/getPortalVersion', async (_, thunkAPI) => {
  try {
    const response = await axios.get<PortalVersionDto>(
      `${process.env.REACT_APP_API_URL}/release-notes/portal-version`,
      {
        params: { portal_type: ReleasePortalType.TELCLOUD },
      }
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Could not fetch the portal version!' });
  }
});
